import { useState } from "react";
import { toast } from "sonner";
import { Phone, MapPin, Clock, Send, CalendarCheck } from "lucide-react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { PageHeader } from "@/components/PageHeader";
import { WhatsAppButton, } from "@/components/WhatsAppButton";
import { WhatsAppIcon } from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { SITE, fullAddress } from "@/config/site";
import { telLink, waLink, WA_MESSAGES } from "@/utils/whatsapp";

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", message: "" });
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const fieldCls =
    "mt-1.5 w-full rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-900 transition-colors focus:border-navy-600 focus:outline-none focus:ring-2 focus:ring-navy-100";

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim()) {
      toast.error("Please enter your name and message.");
      return;
    }
    const message = [
      `Hello ${SITE.name},`,
      "",
      `Name: ${form.name.trim()}`,
      form.phone.trim() ? `Phone: ${form.phone.trim()}` : null,
      "",
      form.message.trim(),
    ].filter(Boolean).join("\n");
    window.open(waLink(message), "_blank", "noopener,noreferrer");
    toast.success("Opening WhatsApp with your message…");
  };

  return (
    <>
      <Seo
        title={`Contact Us — ${SITE.name}`}
        siteName={SITE.name}
        description={`Contact ${SITE.name} in Kasdol, Chhattisgarh. Call or WhatsApp ${SITE.phoneDisplay}, get directions and send an enquiry.`}
      />
      <PageHeader
        title="Contact Us"
        description="We are here to help — call, WhatsApp or visit us in Kasdol."
        crumbs={[{ label: "Contact" }]}
      />

      <section className="py-14 sm:py-20" data-testid="contact-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Info cards */}
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="contact-card-call">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-navy-800 text-white">
                <Phone className="h-5 w-5" aria-hidden="true" />
              </span>
              <h2 className="mt-4 font-heading text-base font-semibold text-navy-900">Call Us</h2>
              <a href={telLink} className="mt-1 block text-sm font-medium text-emerald-600 hover:text-emerald-700" data-testid="contact-phone-link">
                {SITE.phoneDisplay}
              </a>
              <p className="mt-1 text-xs text-slate-500">Tap to call directly</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="contact-card-whatsapp">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#25D366] text-white">
                <WhatsAppIcon className="h-5 w-5" />
              </span>
              <h2 className="mt-4 font-heading text-base font-semibold text-navy-900">WhatsApp</h2>
              <a href={waLink(WA_MESSAGES.general)} target="_blank" rel="noopener noreferrer" className="mt-1 block text-sm font-medium text-emerald-600 hover:text-emerald-700" data-testid="contact-whatsapp-link">
                {SITE.phoneDisplay}
              </a>
              <p className="mt-1 text-xs text-slate-500">Fastest way to reach us</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="contact-card-location">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-600 text-white">
                <MapPin className="h-5 w-5" aria-hidden="true" />
              </span>
              <h2 className="mt-4 font-heading text-base font-semibold text-navy-900">Visit Us</h2>
              <p className="mt-1 text-sm font-medium text-slate-700">{fullAddress()}</p>
              <p className="mt-1 text-xs text-slate-500">Exact street address will be updated soon — please call for directions.</p>
            </div>
          </div>

          <div className="mt-12 grid gap-10 lg:grid-cols-2">
            {/* Form */}
            <form onSubmit={handleSubmit} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8" data-testid="contact-form" noValidate>
              <h2 className="font-heading text-xl font-bold text-navy-900">Send us a message</h2>
              <p className="mt-2 text-sm text-slate-500">Your message opens in WhatsApp on your device — nothing is stored on this website.</p>
              <div className="mt-6 space-y-5">
                <div>
                  <Label htmlFor="contact-name">Your Name *</Label>
                  <Input id="contact-name" value={form.name} onChange={set("name")} placeholder="Full name" className={fieldCls} data-testid="contact-name-input" />
                </div>
                <div>
                  <Label htmlFor="contact-phone">Phone Number</Label>
                  <Input id="contact-phone" type="tel" value={form.phone} onChange={set("phone")} placeholder="Your phone number (optional)" className={fieldCls} data-testid="contact-phone-input" />
                </div>
                <div>
                  <Label htmlFor="contact-message">Message *</Label>
                  <Textarea id="contact-message" value={form.message} onChange={set("message")} rows={4} placeholder="How can we help you?" className={fieldCls} data-testid="contact-message-input" />
                </div>
              </div>
              <button
                type="submit"
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-navy-800 px-6 py-3.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-navy-700 hover:shadow-lg"
                data-testid="contact-submit-button"
              >
                <Send className="h-4 w-4" aria-hidden="true" /> Send Message on WhatsApp
              </button>
              <Link to="/appointments" className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border-2 border-navy-200 px-6 py-3 text-sm font-semibold text-navy-800 transition-colors hover:border-navy-800" data-testid="contact-appointment-link">
                <CalendarCheck className="h-4 w-4" aria-hidden="true" /> Or book an appointment
              </Link>
            </form>

            {/* Map + hours */}
            <div className="space-y-6">
              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid="contact-map">
                <iframe
                  title={`Map of ${SITE.name}, ${SITE.location}`}
                  src={`https://www.google.com/maps?q=${encodeURIComponent(SITE.mapQuery)}&output=embed`}
                  className="h-72 w-full border-0 sm:h-80"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
                <p className="px-5 py-3 text-xs text-slate-500">
                  Map shows Kasdol town. The exact hospital location will be pinned once the address is confirmed.
                </p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-6">
                <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-navy-900">
                  <Clock className="h-4 w-4 text-emerald-600" aria-hidden="true" /> OPD Hours
                </h2>
                <ul className="mt-4 space-y-3">
                  {SITE.hours.map((h) => (
                    <li key={h.label} className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3 text-sm">
                      <span className="font-medium text-navy-900">{h.label}</span>
                      <span className="text-slate-600">{h.value}{h.isDemo && <span className="text-slate-400"> (demo)</span>}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
